// Plan 4.10 — display helpers for RegistrationAttendee rows that can
// represent either a FamilyMember or an EventGuest (XOR).

import type { RegistrationAttendee, FamilyMember } from '../types';
import { EVENT_GUEST_RELATIONSHIP_LABELS, type EventGuestRelationship } from '../types/eventGuest';

/**
 * Returns the display name for an attendee row.
 * - FamilyMember: `familyMember.name`
 * - EventGuest:   `firstName lastName` (guest's own redaction stamps
 *                 "[Removed]" into both names at the DB level)
 * - Neither:      'Unknown'
 */
export function attendeeName(attendee: RegistrationAttendee): string {
  if (attendee.familyMember) return attendee.familyMember.name;
  if (attendee.eventGuest) {
    const g = attendee.eventGuest;
    if (g.isRedacted) return '[Removed] (Guest)';
    return `${g.firstName} ${g.lastName}`.trim();
  }
  return 'Unknown';
}

/**
 * Returns a human-readable relationship label for the attendee.
 * For FamilyMember rows we don't translate here (caller can apply
 * RELATIONSHIP_LABELS itself); for guests we resolve the 12-value
 * EVENT_GUEST_RELATIONSHIP_LABELS, with the relationshipOther fallback.
 * Empty string when neither relation is available.
 */
export function attendeeRelationshipLabel(attendee: RegistrationAttendee): string {
  if (attendee.familyMember) return attendee.familyMember.relationship;
  if (attendee.eventGuest) {
    const g = attendee.eventGuest;
    if (g.relationship === 'Other' && g.relationshipOther) return g.relationshipOther;
    return (
      EVENT_GUEST_RELATIONSHIP_LABELS[g.relationship as EventGuestRelationship] ??
      g.relationship
    );
  }
  return '';
}

/** Convenience: true when this attendee row is a guest (not a family member). */
export function isGuestAttendee(attendee: RegistrationAttendee): boolean {
  return !!attendee.eventGuest;
}

// ── Roster de-duplication + angel exclusion ────────────────────────────────
// Fixes two production faults (reported June 2026):
//   1. The same person appears twice on the attendee list. The CRM→App
//      roster sync writes two rows per warrior child ('child' + 'Warrior')
//      and two per primary contact ('parent' + their CRM role, e.g.
//      'mother'), because two sync code paths each write rows and don't
//      reconcile. We collapse to one row per person.
//   2. Angels (children who have passed) must NEVER be offered as an event
//      attendee — distressing for bereaved families. We exclude every row
//      belonging to an angel from the bookable list.
//
// This is a defensive client-side guard. The canonical fix is "one row per
// person" in the App Backend roster sync; this helper guarantees families
// never see a regression even if a stray duplicate slips through.

const SYNTHETIC_RELATIONSHIPS = new Set(['parent', 'child']);

function normalizeName(name: string | undefined | null): string {
  return (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function relLower(rel: unknown): string {
  return String(rel ?? '').trim().toLowerCase();
}

/** True when this roster row represents an Angel (a child who has passed). */
export function isAngelMember(member: Pick<FamilyMember, 'relationship'>): boolean {
  return relLower(member.relationship) === 'angel';
}

/**
 * Returns the family members to show as bookable event attendees:
 * one row per living person, with angels removed entirely.
 *
 * Dedupe key is the normalized name (the only field shared across a
 * person's duplicate rows — their `crmJourneyId`s differ because one row
 * keys on WarriorId and the other on the member id). When a person has
 * several rows we keep a non-synthetic one ('Warrior'/'Mother'/… over the
 * synthetic 'parent'/'child' labels) so the surviving entry carries the
 * most meaningful relationship and the id the attendance flow expects.
 */
export function visibleAttendees(members: FamilyMember[] | undefined | null): FamilyMember[] {
  if (!Array.isArray(members)) return [];

  // Names belonging to an angel — every row under that name is excluded.
  const angelNames = new Set(
    members.filter(isAngelMember).map((m) => normalizeName(m.name)),
  );

  const byPerson = new Map<string, FamilyMember>();
  for (const m of members) {
    const name = normalizeName(m.name);
    if (!name || angelNames.has(name)) continue; // skip blanks + angels

    const existing = byPerson.get(name);
    if (!existing) {
      byPerson.set(name, m);
      continue;
    }
    // Prefer a non-synthetic relationship over a synthetic 'parent'/'child'.
    const existingSynthetic = SYNTHETIC_RELATIONSHIPS.has(relLower(existing.relationship));
    const candidateSynthetic = SYNTHETIC_RELATIONSHIPS.has(relLower(m.relationship));
    if (existingSynthetic && !candidateSynthetic) {
      byPerson.set(name, m);
    }
  }
  return Array.from(byPerson.values());
}
